import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { supabase } from '../../supabaseClient';
import { setCurrentOrg, setRole } from '../../store';
import { isWorkspaceId, loadClientWorkspace, legacyClientOrgId } from '../../utils/clientWorkspace';
import { ClientWorkspaceContext } from './ClientWorkspaceContext';

function ClientUnavailable({ hasClient, onRetry }) {
  return <Box sx={{ maxWidth: 560, mx: 'auto', mt: 8, p: 3, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ mb: 1 }}>{hasClient ? 'Client unavailable' : 'Select a client'}</Typography>
    {hasClient ? <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>This client could not be loaded. The link may be invalid, you may not have access, or the connection may be unavailable.</Alert>
      : <Typography sx={{ color: '#6B7280', mb: 3 }}>Open a client from the Client List to view their consultation.</Typography>}
    {onRetry && <Button onClick={onRetry} sx={{ mr: 1 }}>Retry</Button>}
    <Button component={Link} to="/consultation" variant="contained">Go to Client List</Button>
  </Box>;
}

function ResolvedClientWorkspace({ patientId, orgId, userId, children }) {
  const dispatch = useDispatch();
  const scope = JSON.stringify([userId, patientId, orgId]);
  const [result, setResult] = useState(null);
  const [retry, setRetry] = useState(0);
  const validIds = isWorkspaceId(patientId) && isWorkspaceId(orgId);

  useEffect(() => {
    if (!validIds || !userId) return undefined;
    let disposed = false;
    const controller = new AbortController();
    setResult(null);
    const deadline = setTimeout(() => {
      controller.abort();
      if (!disposed) setResult({ scope, error: true });
    }, 20000);
    loadClientWorkspace(supabase, { patientId, orgId, userId, signal: controller.signal }).then(workspace => {
      if (disposed || controller.signal.aborted) return;
      // Redux memory is local to this tab, but redux-persist writes to shared
      // localStorage. Align this tab's drawer and pin the workspace context so
      // cached/persisted org or role changes cannot retarget clinical writes.
      dispatch(setCurrentOrg(workspace.currentOrg));
      dispatch(setRole(workspace.role));
      setResult({ scope, workspace });
    }).catch(() => {
      if (!disposed) setResult({ scope, error: true });
    }).finally(() => clearTimeout(deadline));
    return () => { disposed = true; clearTimeout(deadline); controller.abort(); };
  }, [validIds, patientId, orgId, userId, scope, retry, dispatch]);

  if (validIds && (!userId || result?.scope !== scope)) {
    return <Box role="status" sx={{ mt: 8, textAlign: 'center' }}><CircularProgress size={28} /><Typography sx={{ mt: 2 }}>Loading client…</Typography></Box>;
  }
  if (validIds && result?.scope === scope && result.workspace) {
    return <ClientWorkspaceContext.Provider key={scope} value={result.workspace}>{children}</ClientWorkspaceContext.Provider>;
  }
  return <ClientUnavailable hasClient onRetry={validIds ? () => setRetry(value => value + 1) : undefined} />;
}

function LegacyClientWorkspace({ patient, userId, children }) {
  // Resolve only an unambiguous identifier hint from old history entries.
  // Freeze it for this mounted client; mutable Redux is never the write scope.
  // The loader still verifies membership and the authoritative patient row.
  const [orgId] = useState(() => legacyClientOrgId(patient));
  return <ResolvedClientWorkspace patientId={patient.id} orgId={orgId} userId={userId}>{children}</ResolvedClientWorkspace>;
}

export default function ClientWorkspaceRoute({ children }) {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const hasDeepLink = query.has('client') || query.has('org');
  const userId = useSelector(state => state.globalState.userDetails?.id);
  if (hasDeepLink) {
    return <ResolvedClientWorkspace patientId={query.get('client')} orgId={query.get('org')} userId={userId}>{children}</ResolvedClientWorkspace>;
  }
  const legacyPatient = location.state?.params?.patient;
  if (legacyPatient?.id) {
    return <LegacyClientWorkspace key={`${userId}:${legacyPatient.id}`} patient={legacyPatient} userId={userId}>{children}</LegacyClientWorkspace>;
  }
  // Actual onboarding demos stay on /consultation?tour=...&demo=... and are
  // rendered by TourPatientDemo. A fake ID in history state is not a bypass.
  return <ClientUnavailable hasClient={false} />;
}
